/**
 * OutboxDemoPanel — C056 Transactional Outbox 패턴 라이브 데모 (eraf-messaging-kafka 동거)
 * 계약: docs/common-specs/eraf-messaging-kafka/C056-outbox-pattern.md §Δ.4
 * Kafka 패널(KafkaDemoPanel) 동형 — status 카드 → 메시지 기록(enqueue) → 진행 단계 확인.
 * ★ 핵심: enqueue(record) 는 발행하지 않는다 — 커밋된 PENDING 행만 릴레이가 폴링 발행(published) 후
 * 데모 컨슈머가 수신(consumed)한다. record 직후에는 recorded 만 true 이고, 이후
 * relayed/{outboxId} 를 5초 간격으로 폴링(최대 ~30s)하며 recorded→published→consumed
 * 3단계 진행을 스테퍼로 시각화한다.
 * 5상태: loading(상태조회 skeleton) / empty(기록 전) / error(상태조회 실패 · 기록 실패) /
 *        default(상태 + 기록결과 + 진행 스테퍼) / dialog(N/A — 인라인 콘솔, Kafka 동형)
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useOutboxStatus, useOutboxRecord, useOutboxRelayed } from '../hooks/useOutboxDemo';
import { outboxFormSchema, type OutboxFormValues } from '../schema/showcaseSchema';
import type { ApiError } from '../types/showcase';

const OUTBOX_MAX_POLL_ATTEMPTS = 6;

export function OutboxDemoPanel() {
  const { t } = useTranslation();
  const statusQuery = useOutboxStatus();
  const recordMutation = useOutboxRecord();

  const [recordError, setRecordError] = useState<ApiError | null>(null);
  const [outboxId, setOutboxId] = useState<number | null>(null);
  const [pollCount, setPollCount] = useState(0);

  const relayedQuery = useOutboxRelayed(outboxId);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<OutboxFormValues>({
    resolver: zodResolver(outboxFormSchema),
    defaultValues: { message: '' },
  });

  // 새 기록마다 폴링 카운트 리셋
  useEffect(() => {
    setPollCount(0);
  }, [outboxId]);

  // relayedQuery 가 새 데이터를 받을 때마다 카운트 (스테퍼 안내 문구 판단용)
  useEffect(() => {
    if (relayedQuery.dataUpdatedAt) {
      setPollCount((c) => c + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relayedQuery.dataUpdatedAt]);

  const status = statusQuery.data;
  const isStatusLoading = statusQuery.isLoading;
  const isStatusError = statusQuery.isError;
  const disabled = !status?.enabled;

  const relayed = relayedQuery.data;
  const consumed = !!relayed?.consumed;
  const published = !!relayed?.published;
  const recorded = !!recordMutation.data;
  const pollTimedOut = !consumed && pollCount >= OUTBOX_MAX_POLL_ATTEMPTS;

  function onSubmit(values: OutboxFormValues) {
    setRecordError(null);
    setOutboxId(null);
    recordMutation.mutate(
      { message: values.message },
      {
        onSuccess: (data) => {
          reset({ message: '' });
          setOutboxId(data.outboxId);
        },
        onError: (err) => setRecordError(err),
      }
    );
  }

  return (
    <section aria-labelledby="outbox-panel-title">
      <h2
        id="outbox-panel-title"
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-2)',
          marginTop: 0,
        }}
      >
        {t('showcase.outbox.title')}
      </h2>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-6)' }}>
        {t('showcase.outbox.subtitle')}
      </p>

      {/* status — loading skeleton */}
      {isStatusLoading && (
        <div aria-busy="true" aria-label={t('showcase.outbox.status.loadingAriaLabel')} style={cardStyle}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              style={{
                height: 16,
                width: `${35 + ((i * 15) % 40)}%`,
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                animation: 'pulse 1.5s ease-in-out infinite',
                marginBottom: 'var(--spacing-2)',
              }}
            />
          ))}
        </div>
      )}

      {/* status — error */}
      {isStatusError && (
        <div role="alert" style={{ ...cardStyle, border: '1px solid var(--color-danger)' }}>
          <p style={{ margin: 0, color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>
            {(statusQuery.error as ApiError)?.message ?? t('showcase.outbox.status.loadFailed')}
          </p>
          <button type="button" onClick={() => void statusQuery.refetch()} style={secondaryButtonStyle}>
            {t('showcase.common.retry')}
          </button>
        </div>
      )}

      {/* status — default */}
      {!isStatusLoading && !isStatusError && status && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
          <div style={cardStyle}>
            <h3 style={sectionTitleStyle}>{t('showcase.outbox.status.title')}</h3>
            <div style={infoGridStyle}>
              <InfoField label={t('showcase.outbox.status.enabled')}>
                <span style={{ color: status.enabled ? 'var(--color-status-success)' : 'var(--color-status-error)', fontWeight: 700 }}>
                  {status.enabled ? t('showcase.outbox.status.enabledYes') : t('showcase.outbox.status.enabledNo')}
                </span>
              </InfoField>
              <InfoField label={t('showcase.outbox.status.relayEnabled')}>
                <span style={{ color: status.relayEnabled ? 'var(--color-status-success)' : 'var(--color-text-muted)' }}>
                  {status.relayEnabled ? t('showcase.outbox.status.relayEnabledYes') : t('showcase.outbox.status.relayEnabledNo')}
                </span>
              </InfoField>
              <InfoField label={t('showcase.outbox.status.topic')}>
                <code style={monoStyle}>{status.topic}</code>
              </InfoField>
              <InfoField label={t('showcase.outbox.status.pendingCount')}>
                <code style={monoStyle}>{status.pendingCount}</code>
              </InfoField>
              <InfoField label={t('showcase.outbox.status.sentCount')}>
                <code style={monoStyle}>{status.sentCount}</code>
              </InfoField>
              <InfoField label={t('showcase.outbox.status.consumedCount')}>
                <code style={monoStyle}>{status.consumedCount}</code>
              </InfoField>
            </div>
            {!status.enabled && (
              <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-warning)' }}>
                {t('showcase.outbox.disabledNotice')}
              </p>
            )}
          </div>

          {/* record */}
          <div style={cardStyle}>
            <h3 style={sectionTitleStyle}>{t('showcase.outbox.record.title')}</h3>
            <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ display: 'flex', gap: 'var(--spacing-3)', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{ flex: '1 1 240px' }}>
                <input
                  id="outbox-message"
                  type="text"
                  placeholder={t('showcase.outbox.record.placeholder')}
                  aria-label={t('showcase.outbox.record.title')}
                  aria-invalid={!!errors.message}
                  aria-describedby={errors.message ? 'outbox-message-error' : undefined}
                  disabled={disabled}
                  {...register('message')}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-3)',
                    border: `1px solid ${errors.message ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontFamily: 'var(--font-family-sans)',
                    background: disabled ? 'var(--color-surface-muted)' : 'var(--color-surface-background)',
                    color: 'var(--color-text-primary)',
                    boxSizing: 'border-box',
                  }}
                />
                {errors.message && (
                  <p id="outbox-message-error" role="alert" style={errorTextStyle}>
                    {errors.message.message}
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={disabled || !isValid || recordMutation.isPending}
                aria-busy={recordMutation.isPending}
                style={{
                  ...primaryButtonStyle,
                  opacity: disabled || recordMutation.isPending ? 0.6 : 1,
                  cursor: disabled || recordMutation.isPending ? 'not-allowed' : 'pointer',
                }}
              >
                {recordMutation.isPending ? t('showcase.outbox.record.sending') : t('showcase.outbox.record.button')}
              </button>
            </form>

            {/* record — error */}
            {recordError && !recordMutation.isPending && (
              <div role="alert" style={{ marginTop: 'var(--spacing-3)', ...errorBoxStyle }}>
                {recordError.message ?? t('showcase.outbox.record.error')}
              </div>
            )}

            {/* record — empty (아직 기록 전) */}
            {!recorded && !recordError && !recordMutation.isPending && (
              <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                {t('showcase.outbox.record.empty')}
              </p>
            )}

            {/* record — default (결과) */}
            {recordMutation.data && !recordMutation.isPending && (
              <div
                role="region"
                aria-label={t('showcase.outbox.record.resultLabel')}
                style={{ marginTop: 'var(--spacing-4)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--spacing-3)' }}
              >
                <InfoField label={t('showcase.outbox.record.result.outboxId')}>
                  <code style={monoStyle}>{recordMutation.data.outboxId}</code>
                </InfoField>
                <InfoField label={t('showcase.outbox.record.result.idempotencyKey')}>
                  <code style={monoStyle}>{recordMutation.data.idempotencyKey}</code>
                </InfoField>
                <InfoField label={t('showcase.outbox.record.result.topic')}>
                  <code style={monoStyle}>{recordMutation.data.topic}</code>
                </InfoField>
                <InfoField label={t('showcase.outbox.record.result.key')}>
                  <code style={monoStyle}>{recordMutation.data.key}</code>
                </InfoField>
                <InfoField label={t('showcase.outbox.record.result.recordedAt')}>
                  <code style={monoStyle}>{recordMutation.data.recordedAt}</code>
                </InfoField>
              </div>
            )}
          </div>

          {/* progress stepper */}
          {outboxId !== null && (
            <div style={cardStyle}>
              <h3 style={sectionTitleStyle}>{t('showcase.outbox.progress.title')}</h3>
              <ol
                role="list"
                aria-label={t('showcase.outbox.progress.ariaLabel')}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 'var(--spacing-3)',
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                }}
              >
                <StepItem
                  index={1}
                  done={recorded}
                  active={recorded && !published}
                  label={t('showcase.outbox.progress.step.recorded')}
                  description={t('showcase.outbox.progress.step.recordedDesc')}
                />
                <StepItem
                  index={2}
                  done={published}
                  active={recorded && !published}
                  label={t('showcase.outbox.progress.step.published')}
                  description={t('showcase.outbox.progress.step.publishedDesc')}
                />
                <StepItem
                  index={3}
                  done={consumed}
                  active={published && !consumed}
                  label={t('showcase.outbox.progress.step.consumed')}
                  description={t('showcase.outbox.progress.step.consumedDesc')}
                />
              </ol>

              {!consumed && !pollTimedOut && (
                <p aria-live="polite" style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                  {t('showcase.outbox.progress.pollingNotice')}
                </p>
              )}

              {!consumed && pollTimedOut && (
                <p role="alert" style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-warning)' }}>
                  {t('showcase.outbox.progress.timeoutNotice')}
                </p>
              )}

              {consumed && (
                <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-status-success)' }}>
                  {t('showcase.outbox.progress.doneNotice')}
                </p>
              )}

              {relayed && relayed.retryCount > 0 && (
                <p style={{ marginTop: 'var(--spacing-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                  {t('showcase.outbox.progress.retryCount')}: <code style={monoStyle}>{relayed.retryCount}</code>
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Learning */}
      <div
        style={{
          marginTop: 'var(--spacing-4)',
          padding: 'var(--spacing-3)',
          background: 'var(--color-surface-elevated)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
        }}
      >
        <span aria-hidden="true" style={{ marginRight: 'var(--spacing-2)' }}>📚</span>
        <strong>{t('showcase.panel.learning')}</strong> {t('showcase.outbox.learning')}
      </div>
    </section>
  );
}

function StepItem({
  index,
  done,
  active,
  label,
  description,
}: {
  index: number;
  done: boolean;
  active: boolean;
  label: string;
  description: string;
}) {
  const circleColor = done
    ? 'var(--color-status-success)'
    : active
      ? 'var(--color-brand-primary)'
      : 'var(--color-surface-border)';
  return (
    <li
      style={{
        flex: '1 1 160px',
        minWidth: 140,
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-1)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: circleColor,
            color: 'var(--color-text-inverse)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {done ? '✓' : index}
        </span>
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 700,
            color: done || active ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
          }}
        >
          {label}
        </span>
      </div>
      <p style={{ margin: 0, marginLeft: 32, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
        {description}
      </p>
    </li>
  );
}

function InfoField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>{label}</p>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>{children}</div>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  padding: 'var(--spacing-5)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-background)',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-base)',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  marginBottom: 'var(--spacing-4)',
};

const infoGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: 'var(--spacing-4)',
};

const monoStyle: React.CSSProperties = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-sm)',
};

const primaryButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-brand-primary)',
  color: 'var(--color-text-inverse)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  fontFamily: 'var(--font-family-sans)',
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-xs)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const errorTextStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-danger)',
  marginTop: 'var(--spacing-1)',
};

const errorBoxStyle: React.CSSProperties = {
  padding: 'var(--spacing-3)',
  border: '1px solid var(--color-danger)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-muted)',
  color: 'var(--color-danger)',
  fontSize: 'var(--font-size-sm)',
};
